import React, {useMemo} from "react";

import {DishDetailsItem} from "@apps/dish/interfaces";
import cx from "classnames";

import {useDishDetailsConfig} from "@components/DishDetails";
import {MenuCategoryItem} from "@components/Menu/interfaces";
import MenuItem from "@components/MenuItem";

import CategoryBannerImage from "../CategoryBannerImage/CategoryBannerImage";
import {getSizesAttribute} from "../utils/getSizesAttribute";
import styles from "./CategoryBanner1.scss";

interface CategoryBanner1Props {
  category: MenuCategoryItem;
  items: {
    [key: string]: DishDetailsItem;
  };
  menuItemList?: DishDetailsItem[];
}

export default function CategoryBanner1({
  category,
  items,
  menuItemList,
}: CategoryBanner1Props): JSX.Element {
  const {hasDateSelector} = useDishDetailsConfig();
  const isSingleItemCategory = useMemo(
    () => category.item_ids.length === 1,
    [category]
  );

  const hasTwoItems = useMemo(() => category.item_ids.length === 2, [category]);
  const sizesAttribute = useMemo(() => {
    if (category) {
      return getSizesAttribute({category, hasDateSelector});
    }
  }, [category]);

  return (
    <div
      data-testid={`secondary-banner-${category.id}`}
      className={styles.secondaryBanner}
      style={{backgroundImage: `url(${category.secondary_banner})`}}
    >
      <div className={styles.bannerContainer}>
        {category.background_image && (
          <img
            className={styles.bannerBackground}
            src={category.background_image}
            alt=""
          />
        )}
        <div
          className={cx(
            styles.categoryContentContainer,
            isSingleItemCategory && styles.rowDirection
          )}
        >
          <div
            className={cx(
              styles.categoryContent,
              hasTwoItems && styles.twoItemsCategoryContent
            )}
          >
            {category.banner && <CategoryBannerImage category={category} />}
            <div
              style={{color: category.text_color || "inherit"}}
              className={styles.categoryTitle}
              dangerouslySetInnerHTML={{
                __html: category.display_name,
              }}
            ></div>
            {category.description && (
              <div
                style={{color: category.text_color || "inherit"}}
                className={cx(
                  styles.categoryDescription,
                  hasTwoItems && styles.twoItemsDescription
                )}
                dangerouslySetInnerHTML={{
                  __html: category.description,
                }}
              ></div>
            )}
          </div>
          <section
            className={cx(
              isSingleItemCategory ? styles.oneItemMenu : styles.twoItemsMenu
            )}
          >
            {category.item_ids.map((itemId: number) => {
              return (
                <MenuItem
                  sizesAttribute={sizesAttribute}
                  key={`menu-item-${itemId}`}
                  wrapperClassName={cx(
                    styles.menuItemWrapper,
                    hasTwoItems && styles.twoItemsMenuItem
                  )}
                  item={items[itemId]}
                  menuItemList={menuItemList}
                />
              );
            })}
          </section>
        </div>
      </div>
    </div>
  );
}
